import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Moon, Sun, MessageCircle, Users, Compass } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

const Landing = () => {
  const navigate = useNavigate();
  const { theme, toggleTheme } = useTheme();

  const handleLoginClick = () => {
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#090909] flex flex-col relative">
      {/* Header */}
      <header className="w-full px-6 lg:px-16 py-5 border-b border-gray-100 dark:border-gray-900 bg-white dark:bg-[#090909]">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-black dark:bg-white rounded-xl flex items-center justify-center shadow-sm border border-gray-200 dark:border-gray-600">
              <span className="text-white dark:text-gray-900 text-2xl font-bold leading-none">C</span>
            </div>
            <span className="text-lg font-bold text-black dark:text-white tracking-tight">ClubFinder Social</span>
          </Link>
          <div className="flex items-center space-x-4">
            <span className="px-2.5 py-0.5 bg-red-500 text-white text-[10px] font-bold uppercase tracking-wider rounded animate-pulse">Beta Access</span>
            <button
              onClick={toggleTheme}
              className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-900 transition-colors"
              aria-label="Toggle theme"
            >
              {theme === 'dark' ? (
                <Sun className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              ) : (
                <Moon className="h-5 w-5 text-gray-600 dark:text-gray-400" />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-6 lg:px-16 py-24">
        <div className="max-w-6xl w-full">
          <div className="text-center space-y-20">
            {/* Headline Section */}
            <div className="space-y-6">
              <h1 className="text-6xl md:text-7xl lg:text-8xl font-extrabold text-black dark:text-white leading-[1.1] tracking-tight max-w-5xl mx-auto">
                Social media that helps you build Communities
              </h1>
              <p className="text-xl md:text-2xl text-gray-500 dark:text-gray-400 max-w-2xl mx-auto font-normal leading-relaxed">
                Connect with like-minded people and discover groups that match your interests
              </p>
            </div>

            {/* Feature Cards */}
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto pt-8">
              {/* Card 1 - Find Groups */}
              <div className="group bg-white dark:bg-black rounded-3xl p-10 border-2 border-gray-100 dark:border-gray-900 hover:border-gray-200 dark:hover:border-gray-800 transition-all duration-300 hover:-translate-y-1 text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-orange-500 to-amber-500 mb-8 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 mx-auto">
                  <span className="text-white text-3xl font-bold leading-none">C</span>
                </div>
                <h3 className="text-2xl font-bold text-black dark:text-white mb-4">Find Groups</h3>
                <p className="text-gray-500 dark:text-gray-400 leading-relaxed text-base">
                  Discover communities that match your interests and connect with like-minded people
                </p>
              </div>
              
              {/* Card 2 - Connect */}
              <div className="group bg-white dark:bg-black rounded-3xl p-10 border-2 border-gray-100 dark:border-gray-900 hover:border-gray-200 dark:hover:border-gray-800 transition-all duration-300 hover:-translate-y-1 text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-emerald-500 to-green-500 mb-8 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 mx-auto">
                  <MessageCircle className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-black dark:text-white mb-4">Connect</h3>
                <p className="text-gray-500 dark:text-gray-400 leading-relaxed text-base">
                  Engage in meaningful conversations and build relationships within your communities
                </p>
              </div>
              
              {/* Card 3 - Explore */}
              <div className="group bg-white dark:bg-black rounded-3xl p-10 border-2 border-gray-100 dark:border-gray-900 hover:border-gray-200 dark:hover:border-gray-800 transition-all duration-300 hover:-translate-y-1 text-center">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-slate-600 to-blue-600 mb-8 flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300 mx-auto">
                  <Compass className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-black dark:text-white mb-4">Explore</h3>
                <p className="text-gray-500 dark:text-gray-400 leading-relaxed text-base">
                  Get personalized recommendations to discover new opportunities and experiences
                </p>
              </div>
            </div>

            {/* Stats Section */}
            <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto pt-12 pb-8">
              <div className="text-center">
                <div className="text-5xl font-extrabold text-black dark:text-white mb-2">500+</div>
                <div className="text-sm text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider">Active Groups</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-extrabold text-black dark:text-white mb-2">10K+</div>
                <div className="text-sm text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider">Students</div>
              </div>
              <div className="text-center">
                <div className="text-5xl font-extrabold text-black dark:text-white mb-2">50+</div>
                <div className="text-sm text-gray-500 dark:text-gray-400 font-medium uppercase tracking-wider">Universities</div>
              </div>
            </div>

            {/* CTA Section */}
            <div className="space-y-5 pt-8">
              <button
                onClick={handleLoginClick}
                className="px-10 py-5 bg-black dark:bg-white text-white dark:text-black rounded-2xl font-bold text-lg hover:bg-gray-900 dark:hover:bg-gray-100 transition-all shadow-2xl hover:shadow-3xl flex items-center justify-center space-x-3 mx-auto group"
              >
                <MessageCircle className="h-7 w-7 shrink-0 text-current group-hover:scale-110 transition-transform" strokeWidth={2} />
                <span>Start Connecting</span>
              </button>
              <p className="text-sm text-gray-400 dark:text-gray-500">
                Already have an account?{' '}
                <button
                  onClick={handleLoginClick}
                  className="text-black dark:text-white font-semibold hover:underline"
                >
                  Login
                </button>
              </p>
              <p className="text-sm md:text-base text-gray-400 dark:text-gray-500">
                For Ages 8–24
              </p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full px-6 lg:px-16 py-10 border-t border-gray-100 dark:border-gray-900 bg-white dark:bg-[#090909]">
        <div className="max-w-7xl mx-auto relative text-sm">
          {/* Centered group exactly as before */}
          <div className="flex flex-col sm:flex-row justify-center items-center space-y-4 sm:space-y-0 sm:space-x-10 ml-[8px]">
            <div className="flex items-center space-x-6">
              <Link 
                to="/privacy" 
                className="text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white transition-colors font-medium"
              >
                Privacy
              </Link>
              <Link 
                to="/terms-of-service" 
                className="text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white transition-colors font-medium"
              >
                Terms of Service
              </Link>
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-600">
              &copy; {new Date().getFullYear()} ClubFinder Social. All rights reserved.
            </p>
          </div>

          {/* Blogs, X, and LinkedIn pinned to far right without affecting centering */}
          <div className="hidden sm:flex items-center gap-4 absolute right-0 top-1/2 -translate-y-1/2">
            <Link
              to="/blog"
              className="text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white text-sm font-medium transition-colors"
            >
              Blogs
            </Link>
            <a
              href="https://x.com/clubfindermq"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white text-sm font-medium transition-colors"
              aria-label="Visit ClubFinder Social on X"
            >
              X
            </a>
            <a
              href="https://www.linkedin.com/company/clubfindersocial/?viewAsMember=true"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 dark:text-gray-400 hover:text-[#0a66c2] dark:hover:text-[#0a66c2] text-sm font-medium transition-colors"
              aria-label="Visit ClubFinder Social on LinkedIn"
            >
              in
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
